import { useRef } from "react";

/**
 * MoneyInput component
 * props:
 *  - value: string (raw digits, e.g. "100000")
 *  - onChange: (raw: string) => void
 *  - placeholder, required, min ...
 */
const MoneyInput = ({ value, onChange, placeholder, required, min }) => {
  const inputRef = useRef(null);

  const format = (v) => {
    if (!v) return "";
    // Number may be large -> use Intl to avoid locale surprises
    return new Intl.NumberFormat("en-US").format(Number(v));
  };

  const onlyDigits = (v) => (v ? v.replace(/\D/g, "") : "");

  const handleChange = (e) => {
    const el = e.target;
    const rawValue = el.value;
    const selStart = el.selectionStart;

    // count how many digits are before caret in current raw input
    const digitsBeforeCaret = rawValue.slice(0, selStart).replace(/\D/g, "").length;

    // compute new raw digits (we remove all non-digits)
    const newRaw = onlyDigits(rawValue);

    // notify parent with raw digits
    onChange(newRaw);

    // after render, restore caret position near equivalent digit count
    // use setTimeout 0 to run after value is updated
    setTimeout(() => {
      const formatted = format(newRaw);
      if (!inputRef.current) return;

      // find position in formatted string that corresponds to digitsBeforeCaret
      let digitCount = 0;
      let pos = 0;
      for (; pos < formatted.length; pos++) {
        if (/\d/.test(formatted[pos])) digitCount++;
        if (digitCount === digitsBeforeCaret) {
          pos = pos + 1; // place caret after this digit
          break;
        }
      }
      // if we didn't reach desired digitCount (e.g. caret at end), place at end
      if (digitCount < digitsBeforeCaret) pos = formatted.length;

      try {
        inputRef.current.setSelectionRange(pos, pos);
      } catch (err) {
        // ignore
      }
    }, 0);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      className="form-control"
      value={format(value)}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      min={min}
      inputMode="numeric" // shows numeric keyboard on mobile
    />
  );
};

export default MoneyInput;
